import { Navigate } from 'react-router-dom';
import { useProvince } from '../hooks/useProvince';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { useAuth } from '../contexts/AuthContext';
import { useStrategyGuidance } from '../hooks/useStrategyGuidance';
import { REALM_ITEMS } from '../constants/realmNav';
import { HubCard, type HubCardStat } from '../components/shell/HubCard';
import { formatNumber } from '../utils/format';
import { isProtected } from '../utils/protection';
import { computeExploreCapacity, resolveBindingConstraint } from '../utils/exploreCapacity';
import { isFoodCritical, computeDaysOfFood } from '../utils/alerts';
import type { Province } from '../types/province';
import type { SiblingItem } from '../types/nav';
import type { ActiveStrategyDto } from '../types/strategy';

/**
 * SF-172: widened to the SF-150 `ctx` pattern (`KingdomHub.tsx`'s convention for a hub whose
 * cards read more than one data source) now that Strategy's card depends on `useStrategyGuidance`
 * rather than `province`. Per CLAUDE.md's own SF-150 contract, each branch checks only its own
 * source's null/loading state — never a sibling card's — so the old blanket
 * `if (!province) return { primary: 'Loading…' }` top-of-function gate is gone; each
 * province-dependent case keeps its own guard instead.
 */
function computeStat(item: SiblingItem, ctx: {
  province: Province | null;
  strategy: ActiveStrategyDto | null;
  strategyLoading: boolean;
}): HubCardStat {
  switch (item.key) {
    case 'buildings': {
      if (!ctx.province) return { primary: 'Loading…' };
      const province = ctx.province;
      const barren = province.territory.totals.barren;
      const primary = barren === 0 ? 'All land built' : `${barren} acres barren`;
      if (barren === 0) {
        return { primary };
      }
      const costPerAcre = province.territory.constructionCostPerAcre;
      const canAffordAcres = costPerAcre > 0 ? Math.floor(province.resources.gold / costPerAcre) : 0;
      const secondary = canAffordAcres > 0
        ? `Can build ${formatNumber(Math.min(barren, canAffordAcres))} acres`
        : 'Not enough gold to build';
      return {
        primary,
        secondary,
        secondaryTone: canAffordAcres > 0 ? 'default' : 'warning',
        attention: 'warning',
        ratio: { value: barren, max: province.territory.totals.acres },
      };
    }
    case 'explore': {
      if (!ctx.province) return { primary: 'Loading…' };
      const province = ctx.province;
      const exploring = province.territory.totals.exploration;
      if (exploring > 0) {
        return { primary: `${exploring} acres exploring` };
      }
      const { maxBySoldiers, maxByGold, maxByPool, maxExplorable } = computeExploreCapacity(province);
      if (maxExplorable > 0) {
        return { primary: 'Ready to explore', secondary: `${formatNumber(maxExplorable)} acres available` };
      }
      return {
        primary: 'No explore capacity',
        secondary: `Limited by ${resolveBindingConstraint(maxBySoldiers, maxByGold, maxByPool)}`,
        secondaryTone: 'warning',
      };
    }
    case 'sciences': {
      if (!ctx.province) return { primary: 'Loading…' };
      const province = ctx.province;
      // SF-140 Codebase Context: investments[] is a per-tick batch list (up to 24 entries per
      // branch), not one entry per branch — count distinct branches, not raw array length.
      const count = new Set(province.sciences.investments.map((inv) => inv.scienceType)).size;
      const primary = count === 0 ? 'No research underway' : `${count} tech${count === 1 ? '' : 's'} researching`;
      const unallocated = Math.floor(province.sciences.unallocatedBooks);
      if (unallocated > 0) {
        return {
          primary,
          secondary: `${formatNumber(unallocated)} books unallocated`,
          secondaryTone: 'warning',
          attention: 'warning',
        };
      }
      return { primary };
    }
    case 'military': {
      if (!ctx.province) return { primary: 'Loading…' };
      const province = ctx.province;
      // SF-180: repointed off the retired netOffenseAtHome/netDefenseAtHome. Both fields are
      // optional (pre-deploy Redis cache) — explicit typeof guard renders "—", never `?? 0`.
      const modifiedOffensePoints = province.military.modifiedOffensePoints;
      const modifiedDefensePoints = province.military.modifiedDefensePoints;
      const off = typeof modifiedOffensePoints === 'number' ? formatNumber(modifiedOffensePoints) : '—';
      const def = typeof modifiedDefensePoints === 'number' ? formatNumber(modifiedDefensePoints) : '—';
      const primary = `Off ${off} / Def ${def}`;
      const protectedNow = isProtected(province.metadata.protectionExpiresAtTick, province.sync.currentTick);
      // SF-180 Decision 7: the 1-defense-per-acre floor means the gate's input can no longer
      // literally be 0 for a province with land — "sitting at the floor" (<= acres) is the honest
      // generalization of "wide open" (DME/composition contributing nothing beyond the guaranteed
      // minimum). Decision 8: never fire on a missing value — a false-positive alarm on a stale
      // cache entry is worse than a silently-missing stat.
      // SF-183 Component 8: the gate's own input switches from the away-inclusive
      // modifiedDefensePoints to defenseAtHomePoints — before this spec the two were equal (both
      // away-inclusive), so the gate was accurate; after Components 2/5 land, a province with its
      // whole army out has real (at-home) defense pinned at the land floor while
      // modifiedDefensePoints would still read the stale, pre-send total. The primary "Off/Def"
      // display line above is deliberately untouched — only the gate's input changes.
      const defenseAtHomePoints = province.military.defenseAtHomePoints;
      const isWideOpen = typeof defenseAtHomePoints === 'number' && defenseAtHomePoints <= province.territory.totals.acres;
      if (isWideOpen && !protectedNow) {
        return { primary, secondary: 'Open to attack', secondaryTone: 'critical', attention: 'critical' };
      }
      return { primary };
    }
    case 'strategy': {
      if (ctx.strategyLoading) return { primary: 'Loading…' };
      if (!ctx.strategy) return { primary: 'No active strategy' };
      return { primary: `Active: ${ctx.strategy.name}` };
    }
    case 'economy': {
      if (!ctx.province) return { primary: 'Loading…' };
      const province = ctx.province;
      // Gates on the raw food-critical fact before the reassuring net-gold rate (SF-146 pattern).
      // isFoodCritical() already covers the food===0 edge case a bare foodNet<0 check misses
      // (LazyTickSystem's shortfall-clamp drives foodNet toward 0, not negative, per CLAUDE.md's
      // SF-168 entry).
      if (isFoodCritical(province)) {
        const days = computeDaysOfFood(province.economy, province.resources.food);
        const daysLabel = days === null ? 'critical' : `${Math.max(0, Math.round(days))}d left`;
        return { primary: daysLabel, secondary: 'Food deficit', secondaryTone: 'critical', attention: 'critical' };
      }
      // computeDaysOfFood()'s own headline number, not raw foodNet — see the critical branch's
      // own comment above for why a raw foodNet figure can go silent exactly when it matters.
      const netGold = province.economy.lastTickChanges.goldNet;
      return { primary: `${netGold >= 0 ? '+' : ''}${formatNumber(Math.round(netGold))} gc/day` };
    }
    default:
      return { primary: '' };
  }
}

/**
 * SF-128 Component 3: `/realm` hub screen, routable under `AppShellLayout`. A navigation surface,
 * not a data-display page — the desktop guard fires before any province data check (never flashes
 * a loading state on a direct desktop URL visit), and all five cards always render regardless of
 * any data source's load state; only the stat line degrades to a placeholder.
 *
 * SF-172: `useAuth()`/`useStrategyGuidance()` are called unconditionally above the desktop early
 * return, matching `useProvince()`'s existing placement — the Strategy card's data source is
 * independent of `province` entirely, per `computeStat`'s per-source-not-gated-on-sibling contract.
 */
export function RealmHub() {
  const platform = useAppShellPlatform();
  const { province } = useProvince();
  const { worldId, provinceId } = useAuth();
  const { activeStrategy, isLoading: strategyLoading } = useStrategyGuidance(worldId, provinceId);

  if (platform === 'desktop') {
    return <Navigate to={REALM_ITEMS[0].route} replace />;
  }

  return (
    <div>
      <h1 className="text-lg font-bold text-game-50 mb-4">Realm</h1>
      <div className="flex flex-col gap-3">
        {REALM_ITEMS.map((item) => (
          <HubCard
            key={item.key}
            item={item}
            stat={computeStat(item, { province, strategy: activeStrategy, strategyLoading })}
          />
        ))}
      </div>
    </div>
  );
}
